import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, X, Loader2, Sparkles, Shield, Zap, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for uploaded assets
const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${API}${url.startsWith('/api') ? '' : '/api'}${url}`;
  return `${API}/api/${url}`;
};

// Brand Logo component that uses uploaded logo
const BrandLogo = ({ logoUrl, dark = true }) => (
  <div className="flex items-center gap-2">
    <img src={logoUrl} alt="MedStudyPro" className="h-8 object-contain" />
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

// Default MedStudyPro Logo Component - Updated geometric M design
const DefaultLogo = ({ dark = true }) => (
  <div className="flex items-center gap-2">
    <svg viewBox="0 0 40 40" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left M leg */}
      <path d="M4 32V10L12 18L20 10" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Right M leg */}
      <path d="M20 10L28 18L36 10V32" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Center cube - top face */}
      <path d="M20 16L24 18V22L20 24L16 22V18L20 16Z" fill="#334155"/>
      {/* Center cube - visible faces */}
      <path d="M20 16L24 18L20 20L16 18L20 16Z" fill="#475569"/>
      <path d="M20 20L24 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.6"/>
      <path d="M20 20L16 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.4"/>
    </svg>
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    // Check if user was redirected back after canceling
    if (searchParams.get('canceled') === '1') {
      toast.info('Checkout was canceled. Feel free to try again when ready.');
    }
    
    fetchTiers();
    checkSubscriptionStatus();
    fetchLogo();
  }, [searchParams]);

  const fetchLogo = async () => {
    try {
      const res = await fetch(`${API}/api/logo-settings`);
      if (res.ok) {
        const data = await res.json();
        setLogoUrl(data.logo_url || data.logo_light_url);
      }
    } catch (err) {
      console.error('Failed to fetch logo:', err);
    }
  };

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API}/api/tiers`);
      const data = await res.json();
      setTiers(data.tiers || []);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const res = await fetch(`${API}/api/subscription/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubscriptionStatus(data);
        setIsLoggedIn(data.isAuthenticated === true);
        
        if (data.subscriptionActive) {
          setCurrentSubscription({ tier_name: data.currentPlan });
        }
      } else {
        // Not authenticated - this is expected, don't show error
        setIsLoggedIn(false);
      }
    } catch (error) {
      // Network error - assume not logged in
      console.error('Error checking subscription:', error);
      setIsLoggedIn(false);
    }
  };

  const handleSubscribe = async (tierId, tierName) => {
    // Determine plan ID (use tier name for simplicity)
    const planId = tierName.toLowerCase();
    
    // Store selected plan in localStorage
    localStorage.setItem('selectedPlan', JSON.stringify({ 
      planId, 
      tierId,
      tierName,
      timestamp: Date.now() 
    }));
    
    // Always check login status before proceeding
    if (!isLoggedIn) {
      // Not logged in - redirect to auth with plan param
      navigate(`/auth?plan=${planId}`);
      return;
    }
    
    // Already logged in - check subscription status and proceed to checkout
    if (subscriptionStatus?.subscriptionActive) {
      toast.info(`You already have an active ${subscriptionStatus.currentPlan} subscription.`);
      navigate('/dashboard');
      return;
    }
    
    // Logged in but no subscription - create checkout session
    setSubscribing(tierId);
    
    try {
      const res = await fetch(`${API}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          planId,
          returnUrl: window.location.href,
          couponCode: couponCode.trim().toUpperCase()  // Include coupon code
        })
      });
      
      // Read body only once
      const contentType = res.headers.get('content-type') || '';
      const raw = await res.text();
      let data = null;
      
      if (raw && contentType.includes('application/json')) {
        try {
          data = JSON.parse(raw);
        } catch (e) {
          console.error('JSON parse error:', e);
        }
      }
      
      if (!res.ok) {
        throw new Error((data && (data.detail || data.error || data.message)) || raw || 'Failed to create checkout session');
      }
      
      // Check if this is a beta tier (no Stripe, direct activation)
      if (data && data.is_beta) {
        toast.success(data.message || 'Beta access activated!');
        // Redirect to success page
        window.location.href = data.checkout_url;
        return;
      }
      
      // Redirect to Stripe Checkout for paid tiers
      if (data && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
    } finally {
      setSubscribing(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const formData = new FormData();
      formData.append('origin_url', window.location.origin);
      
      const res = await fetch(`${API}/api/subscriptions/create-portal-session`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      // Read body only once
      const contentType = res.headers.get('content-type') || '';
      const raw = await res.text();
      let data = null;
      
      if (raw && contentType.includes('application/json')) {
        try {
          data = JSON.parse(raw);
        } catch (e) {
          console.error('JSON parse error:', e);
        }
      }
      
      if (!res.ok) {
        throw new Error((data && (data.detail || data.error || data.message)) || raw || 'Failed to open billing portal');
      }
      
      if (data && data.portal_url) {
        window.location.href = data.portal_url;
      }
    } catch (error) {
      toast.error(error.message || 'Failed to open billing portal');
    }
  };

  const getFeatureList = (features, tierName) => {
    const list = [];
    const isBasic = tierName?.toLowerCase() === 'basic';
    const isBetaOrPro = tierName?.toLowerCase() === 'beta' || tierName?.toLowerCase() === 'pro';
    
    if (features.quizzes) list.push({ name: 'All Quizzes', included: true });
    
    // Flashcards for Pro and Beta, not Basic
    if (features.flashcards && isBetaOrPro) {
      list.push({ name: 'Flashcards', included: true });
    } else if (isBasic) {
      list.push({ name: 'Flashcards', included: false });
    }
    
    if (features.games && isBetaOrPro) {
      list.push({ name: 'All Games (Matching, Word Search)', included: true });
    } else {
      list.push({ name: 'Games', included: false });
    }
    
    if (features.case_studies_per_month === 0 && isBetaOrPro) {
      list.push({ name: 'Unlimited Case Studies', included: true });
    } else if (features.case_studies_per_month > 0) {
      list.push({ name: `${features.case_studies_per_month} Case Studies/month`, included: true });
    } else {
      list.push({ name: 'Case Studies', included: false });
    }
    
    list.push({ name: 'Progress Tracking', included: true });
    
    if (features.reference_sheets_pdf && isBetaOrPro) {
      list.push({ name: 'PDF Downloads', included: true });
    } else {
      list.push({ name: 'PDF Downloads', included: false });
    }
    
    return list;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#192A3F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF9E00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#192A3F]">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        {logoUrl ? (
          <BrandLogo logoUrl={getFullUrl(logoUrl)} dark={true} />
        ) : (
          <DefaultLogo dark={true} />
        )}
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          {isLoggedIn ? (
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-[#FF9E00] hover:bg-[#E88D00] text-white"
            >
              Go to Dashboard
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/auth')}
              className="bg-[#FF9E00] hover:bg-[#E88D00] text-white"
            >
              Sign In
            </Button>
          )}
        </div>
      </nav>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-6 py-12 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF9E00]/10 rounded-full mb-6">
          <Sparkles className="w-4 h-4 text-[#FF9E00]" />
          <span className="text-[#FF9E00] text-sm font-medium">Simple, Transparent Pricing</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Choose Your <span className="text-[#FF9E00]">Learning Plan</span>
        </h1>
        <p className="text-white/70 text-lg max-w-2xl mx-auto">
          Start with Basic or unlock the full MedStudyPro experience with Pro.
          Cancel anytime—no long-term commitment.
        </p>
      </div>

      {/* Current Subscription Notice */}
      {currentSubscription && (
        <div className="max-w-4xl mx-auto px-6 mb-8">
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="text-green-400">
                You have an active <strong>{currentSubscription.tier_name}</strong> subscription
              </span>
            </div>
            <Button
              onClick={handleManageSubscription}
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            >
              Manage Subscription
            </Button>
          </div>
        </div>
      )}

      {/* Coupon Code Section */}
      <div className="max-w-4xl mx-auto px-6 mb-8">
        {!showCouponInput ? (
          <button
            onClick={() => setShowCouponInput(true)}
            className="text-[#FF9E00] hover:text-[#E88D00] text-sm font-medium flex items-center gap-2 mx-auto"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            Have a coupon code?
          </button>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 max-w-md mx-auto">
            <label className="block text-white/70 text-sm mb-2">Coupon Code</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter code (e.g., BETA100)"
                className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FF9E00] focus:border-transparent"
              />
              <button
                onClick={() => {
                  setShowCouponInput(false);
                  setCouponCode('');
                }}
                className="px-3 py-2 text-white/50 hover:text-white/70"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {couponCode && (
              <p className="text-[#FF9E00] text-sm mt-2">
                Coupon &quot;{couponCode}&quot; will be applied at checkout
              </p>
            )}
            <p className="text-white/40 text-xs mt-2">
              You can also enter promo codes on the Stripe checkout page
            </p>
          </div>
        )}
      </div>

      {/* Pricing Cards */}
      <div className="max-w-5xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const isBeta = tier.name.toLowerCase() === 'beta';
            const isPopular = tier.highlight || tier.name.toLowerCase() === 'beta';
            const isCurrentPlan = currentSubscription?.tier_name?.toLowerCase() === tier.name.toLowerCase();
            const features = getFeatureList(tier.features, tier.name);
            
            return (
              <div
                key={tier.tier_id}
                className={`relative bg-white rounded-2xl overflow-hidden ${
                  isBeta ? 'ring-2 ring-green-500 shadow-xl shadow-green-500/20' 
                  : isPopular ? 'ring-2 ring-[#FF9E00] shadow-xl shadow-[#FF9E00]/20' 
                  : 'shadow-lg'
                }`}
              >
                {isBeta && (
                  <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-4 py-1 rounded-bl-xl">
                    FREE BETA
                  </div>
                )}
                {!isBeta && isPopular && (
                  <div className="absolute top-0 right-0 bg-[#FF9E00] text-white text-xs font-bold px-4 py-1 rounded-bl-xl">
                    MOST POPULAR
                  </div>
                )}
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1E3A5C] mb-2">{tier.display_name || tier.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{tier.description}</p>
                  
                  <div className="flex items-baseline gap-1 mb-4">
                    {isBeta ? (
                      <>
                        <span className="text-3xl font-bold text-green-600">FREE</span>
                        <span className="text-slate-500 text-sm ml-2">No card required</span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-[#1E3A5C]">${tier.price_monthly}</span>
                        <span className="text-slate-500">/month</span>
                      </>
                    )}
                  </div>
                  
                  <Button
                    onClick={() => handleSubscribe(tier.tier_id, tier.name)}
                    disabled={subscribing === tier.tier_id || isCurrentPlan}
                    className={`w-full h-11 rounded-full font-semibold ${
                      isBeta
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : isPopular
                        ? 'bg-[#FF9E00] hover:bg-[#E88D00] text-white'
                        : 'bg-[#1E3A5C] hover:bg-[#264B73] text-white'
                    }`}
                    data-testid={`subscribe-${tier.name.toLowerCase()}-btn`}
                  >
                    {subscribing === tier.tier_id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : isBeta ? (
                      'Start Free Trial'
                    ) : (
                      'Subscribe'
                    )}
                  </Button>
                  
                  <div className="mt-6 space-y-2">
                    {features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        {feature.included ? (
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <X className="w-4 h-4 text-slate-300 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${feature.included ? 'text-slate-700' : 'text-slate-400'}`}>
                          {feature.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* FAQ / Trust */}
        <div className="mt-16 text-center">
          <div className="flex items-center justify-center gap-8 text-white/60 text-sm">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>Instant Access</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Cancel Anytime</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
